import { useState } from "react";
import { LayoutGrid, Plus, Trash2, Calculator } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { formatEUR } from "@/lib/api";
import { MATERIALS, EDGE_TYPES, CUT_PRICE_M } from "@/components/RazrezConfigurator";
import { useMaterials } from "@/lib/materials";

const PANEL_SIZES = [
  { id: "std", name: "2800 × 2070 mm (iveral / MDF)", w: 2800, h: 2070 },
  { id: "osb", name: "2500 × 1250 mm (OSB)", w: 2500, h: 1250 },
];

function expandPieces(rows) {
  const out = [];
  for (const r of rows) {
    const w = parseInt(r.w, 10);
    const h = parseInt(r.h, 10);
    const qty = parseInt(r.qty, 10);
    if (!w || !h || !qty || w < 50 || h < 50) continue;
    for (let i = 0; i < qty; i++) {
      out.push({ w, h, edging: r.edging, label: `${w}×${h}` });
    }
  }
  return out;
}

function rectContains(a, b) {
  return b.x >= a.x && b.y >= a.y && b.x + b.w <= a.x + a.w && b.y + b.h <= a.y + a.h;
}

function pruneFreeRects(rects) {
  return rects.filter(
    (r, i) => r.w > 0 && r.h > 0 && !rects.some((o, j) => j !== i && rectContains(o, r))
  );
}

function findPosition(panel, piece, kerf, allowRotate) {
  let best = null;
  const candidates = [{ w: piece.w, h: piece.h, rotated: false }];
  if (allowRotate && piece.w !== piece.h) candidates.push({ w: piece.h, h: piece.w, rotated: true });
  for (const c of candidates) {
    const w = c.w + kerf;
    const h = c.h + kerf;
    for (const fr of panel.freeRects) {
      if (w <= fr.w && h <= fr.h) {
        const shortSide = Math.min(Math.abs(fr.w - w), Math.abs(fr.h - h));
        const longSide = Math.max(Math.abs(fr.w - w), Math.abs(fr.h - h));
        const score = shortSide * 100000 + longSide;
        if (!best || score < best.score) {
          best = { x: fr.x, y: fr.y, w, h, rotated: c.rotated, score };
        }
      }
    }
  }
  return best;
}

function placeRect(panel, rect, piece) {
  const newFree = [];
  for (const fr of panel.freeRects) {
    if (fr.x >= rect.x + rect.w || fr.x + fr.w <= rect.x || fr.y >= rect.y + rect.h || fr.y + fr.h <= rect.y) {
      newFree.push(fr);
      continue;
    }
    if (fr.x < rect.x) newFree.push({ x: fr.x, y: fr.y, w: rect.x - fr.x, h: fr.h });
    if (rect.x + rect.w < fr.x + fr.w) newFree.push({ x: rect.x + rect.w, y: fr.y, w: fr.x + fr.w - (rect.x + rect.w), h: fr.h });
    if (fr.y < rect.y) newFree.push({ x: fr.x, y: fr.y, w: fr.w, h: rect.y - fr.y });
    if (rect.y + rect.h < fr.y + fr.h) newFree.push({ x: fr.x, y: rect.y + rect.h, w: fr.w, h: fr.y + fr.h - (rect.y + rect.h) });
  }
  panel.freeRects = pruneFreeRects(newFree);
  panel.placements.push({
    x: rect.x, y: rect.y, w: rect.w, h: rect.h,
    label: piece.label, edging: piece.edging, rotated: rect.rotated,
  });
}

function packAttempt(pieces, PW, PH, kerf, allowRotate) {
  const panels = [];
  const unplaced = [];
  for (const piece of pieces) {
    let bestPanel = null;
    let bestPos = null;
    for (const panel of panels) {
      const pos = findPosition(panel, piece, kerf, allowRotate);
      if (pos && (!bestPos || pos.score < bestPos.score)) {
        bestPos = pos;
        bestPanel = panel;
      }
    }
    if (bestPanel) {
      placeRect(bestPanel, bestPos, piece);
      continue;
    }
    const panel = { freeRects: [{ x: 0, y: 0, w: PW, h: PH }], placements: [] };
    const pos = findPosition(panel, piece, kerf, allowRotate);
    if (pos) {
      placeRect(panel, pos, piece);
      panels.push(panel);
    } else {
      unplaced.push(piece);
    }
  }
  return { panels, unplaced };
}

function packPieces(pieces, PW, PH, kerf, allowRotate) {
  const byArea = [...pieces].sort((a, b) => b.w * b.h - a.w * a.h);
  const byHeight = [...pieces].sort((a, b) => b.h - a.h || b.w * b.h - a.w * a.h);
  const attempts = [packAttempt(byArea, PW, PH, kerf, allowRotate)];
  const second = packAttempt(byHeight, PW, PH, kerf, allowRotate);
  attempts.push(second);
  attempts.sort((a, b) => a.panels.length - b.panels.length || a.unplaced.length - b.unplaced.length);
  return attempts[0];
}

const inputDark = "w-full h-10 px-3 rounded-lg bg-[#1A1D1A] border border-[#323832] text-[#FBF9F5] text-sm font-tech focus:outline-none focus:ring-2 focus:ring-[#D97706]/50";

export default function OptimizatorRazreza() {
  const { addItem } = useCart();
  const materials = useMaterials();
  const [rows, setRows] = useState([
    { w: "800", h: "400", qty: "4", edging: false },
    { w: "1200", h: "600", qty: "2", edging: false },
  ]);
  const [materialId, setMaterialId] = useState("iveral18");
  const [panelId, setPanelId] = useState("std");
  const [edgeTypeId, setEdgeTypeId] = useState("abs1");
  const [kerf, setKerf] = useState("4");
  const [allowRotate, setAllowRotate] = useState(false);
  const [result, setResult] = useState(null);

  const setRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const calculate = () => {
    const pieces = expandPieces(rows);
    if (pieces.length === 0) {
      toast.error("Vnesite vsaj en kos z veljavnimi dimenzijami (min. 50 mm)");
      return;
    }
    const material = materials.find((m) => m.id === materialId) || MATERIALS[0];
    const basePanel = PANEL_SIZES.find((p) => p.id === panelId);
    const panel = material.fromArticle
      ? { w: material.panelW, h: material.panelH }
      : basePanel;
    const edgeType = EDGE_TYPES.find((e) => e.id === edgeTypeId);
    const kerfMm = Math.max(0, parseInt(kerf, 10) || 0);
    const { panels, unplaced } = packPieces(pieces, panel.w, panel.h, kerfMm, allowRotate);

    const panelAreaM2 = (panel.w * panel.h) / 1e6;
    const usedM2 = pieces.reduce((s, p) => (unplaced.includes(p) ? s : s + (p.w * p.h) / 1e6), 0);
    const cutM = pieces.reduce((s, p) => s + (2 * (p.w + p.h)) / 1000, 0);
    const edgeM = pieces.reduce((s, p) => (p.edging ? s + (2 * (p.w + p.h)) / 1000 : s), 0);
    const materialCost = panels.length * panelAreaM2 * material.priceM2;
    const cutCost = cutM * CUT_PRICE_M;
    const edgeCost = edgeM * edgeType.priceM;

    setResult({
      panels,
      unplaced,
      panel,
      material,
      edgeType,
      piecesCount: pieces.length,
      usedM2,
      panelAreaM2,
      util: panels.length ? (usedM2 / (panels.length * panelAreaM2)) * 100 : 0,
      wasteM2: panels.length * panelAreaM2 - usedM2,
      cutM,
      edgeM,
      materialCost,
      cutCost,
      edgeCost,
      total: materialCost + cutCost + edgeCost,
    });
  };

  const handleAdd = () => {
    if (!result) return;
    addItem({
      key: `custom-opt-${Date.now()}`,
      name: `OPTIMIZIRAN RAZREZ – ${result.material.name}`,
      price: Math.round(result.total * 100) / 100,
      qty: 1,
      unit: "paket",
      image: "https://images.unsplash.com/photo-1560846389-956694677531?auto=format&fit=crop&w=400&q=80",
      meta: `${result.panels.length}× plošča ${result.panel.w}×${result.panel.h} mm · ${result.piecesCount} kosov · razrez ${result.cutM.toFixed(1)} m${result.edgeM > 0 ? ` · robljenje ${result.edgeType.name} ${result.edgeM.toFixed(1)} m` : ""}`,
    });
    toast.success("Optimiziran razrez dodan v košarico", {
      description: `${result.panels.length} plošč, ${result.piecesCount} kosov — ${formatEUR(result.total)}`,
    });
  };

  return (
    <div className="mt-16 pt-14 border-t border-[#323832]" data-testid="optimizer-section">
      <div className="max-w-2xl mb-10">
        <p className="font-tech text-xs uppercase tracking-[0.2em] text-[#D97706] mb-3 flex items-center gap-2">
          <LayoutGrid className="w-4 h-4" /> Optimizacija razreza
        </p>
        <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-[#FBF9F5] leading-snug">
          Seznam kosov — dejanska poraba plošč
        </h3>
        <p className="mt-3 text-sm text-stone-400 leading-relaxed">
          Vnesite vse kose vašega projekta in izračun vam pokaže, koliko plošč potrebujete, kako so kosi razporejeni ter kolikšen je odpad. Razporeditev je informativna — končni plan razreza potrdimo pred rezom.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7 bg-[#232723] border border-[#323832] rounded-2xl p-6 sm:p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-tech uppercase tracking-wider text-stone-400 block mb-2">Material</label>
              <select data-testid="opt-material-select" value={materialId} onChange={(e) => setMaterialId(e.target.value)} className={inputDark}>
                {materials.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} — {formatEUR(m.priceM2)}/m²</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-tech uppercase tracking-wider text-stone-400 block mb-2">Format plošče</label>
              <select data-testid="opt-panel-select" value={panelId} onChange={(e) => setPanelId(e.target.value)} className={inputDark}>
                {PANEL_SIZES.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {materials.find((m) => m.id === materialId)?.fromArticle && (
                <p className="mt-2 text-xs text-[#D97706]" data-testid="opt-article-format-note">
                  Format se vzame iz izbranega artikla ({materials.find((m) => m.id === materialId).panelW} × {materials.find((m) => m.id === materialId).panelH} mm).
                </p>
              )}
            </div>
          </div>

          <div>
            <div className="grid grid-cols-[1fr_1fr_90px_auto_40px] gap-2 mb-2 text-xs font-tech uppercase tracking-wider text-stone-400">
              <span>Dolžina (mm)</span>
              <span>Širina (mm)</span>
              <span>Kos</span>
              <span>Robljenje</span>
              <span />
            </div>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_90px_auto_40px] gap-2 items-center" data-testid={`opt-row-${i}`}>
                  <input data-testid={`opt-w-${i}`} type="number" min="50" value={r.w} onChange={(e) => setRow(i, { w: e.target.value })} className={inputDark} placeholder="npr. 800" />
                  <input data-testid={`opt-h-${i}`} type="number" min="50" value={r.h} onChange={(e) => setRow(i, { h: e.target.value })} className={inputDark} placeholder="npr. 400" />
                  <input data-testid={`opt-qty-${i}`} type="number" min="1" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} className={inputDark} />
                  <label className="flex items-center gap-1.5 text-xs text-stone-400 cursor-pointer">
                    <input
                      data-testid={`opt-edging-${i}`}
                      type="checkbox"
                      checked={r.edging}
                      onChange={(e) => setRow(i, { edging: e.target.checked })}
                      className="accent-[#D97706] w-4 h-4"
                    />
                    vse str.
                  </label>
                  <button
                    data-testid={`opt-remove-${i}`}
                    onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                    className="w-9 h-9 flex items-center justify-center text-stone-500 hover:text-red-500 transition-colors"
                    aria-label="Odstrani vrstico"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              data-testid="opt-add-row-btn"
              onClick={() => setRows((prev) => [...prev, { w: "", h: "", qty: "1", edging: false }])}
              className="mt-3 flex items-center gap-2 text-sm font-semibold text-[#D97706] hover:text-[#F59E0B] transition-colors"
            >
              <Plus className="w-4 h-4" /> Dodaj kos
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-4 pt-2">
            <div>
              <label className="text-xs font-tech uppercase tracking-wider text-stone-400 block mb-2">Robni trak</label>
              <select data-testid="opt-edge-select" value={edgeTypeId} onChange={(e) => setEdgeTypeId(e.target.value)} className={inputDark}>
                {EDGE_TYPES.map((e) => (
                  <option key={e.id} value={e.id}>{e.name} · {formatEUR(e.priceM)}/m</option>
                ))}
              </select>
            </div>
            <div className="w-28">
              <label className="text-xs font-tech uppercase tracking-wider text-stone-400 block mb-2">Rez žage (mm)</label>
              <input data-testid="opt-kerf-input" type="number" min="0" max="10" value={kerf} onChange={(e) => setKerf(e.target.value)} className={inputDark} />
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer pb-2.5">
              <input
                data-testid="opt-rotate-toggle"
                type="checkbox"
                checked={allowRotate}
                onChange={(e) => setAllowRotate(e.target.checked)}
                className="accent-[#D97706] w-4 h-4"
              />
              Dovoljeno obračanje kosov
            </label>
          </div>

          <button
            data-testid="opt-calculate-btn"
            onClick={calculate}
            className="w-full h-12 rounded-full bg-[#D97706] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#B45309] transition-colors"
          >
            <Calculator className="w-4 h-4" /> Izračunaj optimizacijo
          </button>
        </div>

        <div className="lg:col-span-5">
          {!result ? (
            <div className="bg-[#232723] border border-dashed border-[#323832] rounded-2xl p-10 text-center text-stone-500 text-sm" data-testid="opt-results-empty">
              Vnesite kose in kliknite »Izračunaj optimizacijo« — tu se bo prikazala razporeditev po ploščah.
            </div>
          ) : (
            <div className="space-y-6" data-testid="opt-results">
              <div className="bg-[#FBF9F5] rounded-2xl p-6 sm:p-8">
                <h4 className="font-display font-bold text-lg text-[#1A1D1A] mb-4">Rezultat optimizacije</h4>
                <dl className="space-y-2.5 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-stone-600">Potrebne plošče ({result.panel.w}×{result.panel.h} mm)</dt>
                    <dd className="font-tech font-bold text-lg text-[#B45309]" data-testid="opt-panels-count">{result.panels.length}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-stone-600">Izkoriščenost materiala</dt>
                    <dd className="font-tech font-semibold" data-testid="opt-utilization">{result.util.toFixed(1)} %</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-stone-600">Odpad</dt>
                    <dd className="font-tech font-semibold" data-testid="opt-waste">{result.wasteM2.toFixed(2)} m²</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-stone-600">Material ({result.panels.length} × {result.panelAreaM2.toFixed(2)} m²)</dt>
                    <dd className="font-tech font-semibold">{formatEUR(result.materialCost)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-stone-600">Razrez ({result.cutM.toFixed(1)} m)</dt>
                    <dd className="font-tech font-semibold">{formatEUR(result.cutCost)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-stone-600">Robljenje ({result.edgeM.toFixed(1)} m)</dt>
                    <dd className="font-tech font-semibold">{formatEUR(result.edgeCost)}</dd>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-[#E4E0D7] font-display font-bold text-lg">
                    <dt>Skupaj z DDV</dt>
                    <dd className="font-tech text-[#B45309]" data-testid="opt-total">{formatEUR(result.total)}</dd>
                  </div>
                </dl>
                {result.unplaced.length > 0 && (
                  <p className="mt-4 text-xs text-red-600 bg-red-50 rounded-lg p-3" data-testid="opt-unplaced">
                    {result.unplaced.length} kosov ni bilo mogoče razporediti (preveliki za izbran format plošče).
                  </p>
                )}
                <button
                  data-testid="opt-add-to-cart-btn"
                  onClick={handleAdd}
                  className="mt-5 w-full h-12 rounded-full bg-[#2D4030] text-[#FBF9F5] font-semibold hover:bg-[#1E2C20] transition-colors"
                >
                  Dodaj celoten razrez v košarico
                </button>
              </div>

              <div className="space-y-4">
                {result.panels.map((p, i) => (
                  <div key={i} data-testid={`opt-panel-svg-${i}`}>
                    <p className="text-xs font-tech uppercase tracking-wider text-stone-400 mb-2">
                      Plošča {i + 1} — {p.placements.length} kosov
                    </p>
                    <svg
                      viewBox={`-20 -20 ${result.panel.w + 40} ${result.panel.h + 40}`}
                      className="w-full bg-[#FBF9F5] rounded-xl border border-[#323832]"
                    >
                      {p.placements.map((pl, j) => (
                        <g key={j}>
                          <rect
                            x={pl.x} y={pl.y} width={pl.w} height={pl.h}
                            fill="#D97706" fillOpacity={pl.edging ? 0.45 : 0.2}
                            stroke="#B45309" strokeWidth="8"
                          />
                          <text
                            x={pl.x + pl.w / 2} y={pl.y + pl.h / 2}
                            textAnchor="middle" dominantBaseline="middle"
                            fontSize="80" fill="#1A1D1A" fontFamily="JetBrains Mono, monospace"
                          >
                            {pl.label}
                          </text>
                        </g>
                      ))}
                    </svg>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
